import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { of, Subscription } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { map, switchMap } from 'rxjs/internal/operators';
import { Address } from '../../../../account/models/Address';
import { AnreService } from '../../../service/anre.service';
import { InternalDocument } from '../../../../../shared/model/internal-document';
import { CountyDTO } from 'src/app/components/shared/models/CountyDTO';
import { CityDTO } from 'src/app/components/shared/models/CityDTO';
import { AddressService } from 'src/app/components/shared/services/address.service';
import { DocumentService } from 'src/app/shared/service/document.service';

@Component({
  selector: 'app-operator-supplier-validation',
  templateUrl: './operator-supplier-validation.component.html',
  styleUrls: ['./operator-supplier-validation.component.scss']
})
export class OperatorSupplierValidationComponent implements OnInit, OnDestroy {

  public validationForm: FormGroup;
  public validationDetails: any;
  public routeDataSub: Subscription;
  public isValidated = false;
  private taskId: number;
  public editable = false;
  public fileList: InternalDocument[] = [];

  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];
  public citiesCorrespondence: CityDTO[] = [];

  constructor(
    public documentService: DocumentService,
    private formBuilder: FormBuilder,
    private anreService: AnreService,
    private router: Router,
    private addressService: AddressService,
    private route: ActivatedRoute
  ) {
    this.createOperatorForm();
  }

  createOperatorForm(): void {
    this.validationForm = this.formBuilder.group({
      code: [''],
      number: [''],
      name: ['', [Validators.required]],
      phoneNumber: [''],
      mobileNumber: [''],
      fax: [''],
      cui: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      onrcRegistrationNumber: ['', [Validators.required]],
      email: [''],
      legalForm: ['', [Validators.required]],
      licenseGas: [''],
      licenseElectricity: [''],
      // address
      county: ['', [Validators.required]],
      city: ['', [Validators.required]],
      streetName: ['', [Validators.required]],
      streetNumber: ['', [Validators.required]],
      block: [''],
      apartment: [''],
      floor: [''],
      extendedAddress: [''],
      postalCode: [''],
      // correspondenceAddress
      sameAddress: [''],
      correspondenceCounty: ['', [Validators.required]],
      correspondenceCity: ['', [Validators.required]],
      correspondenceStreetName: ['', [Validators.required]],
      correspondenceStreetNumber: ['', [Validators.required]],
      correspondenceBlock: [''],
      correspondenceFloor: [''],
      correspondenceApartment: [''],
      correspondenceExtendedAddress: [''],
      correspondencePostalCode: [''],
      // person
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      cnp: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(13)]],
      // validation
      validated: [''],
      invalidationReason: ['']
    });
  }

  ngOnInit(): void {

    this.addressService.getCounties().subscribe(data => this.counties = data);
    this.routeDataSub = this.route
      .paramMap
      .pipe(
        map(value => {
          this.editable = !!window.history.state.editable;
          this.taskId = window.history.state.taskId;
          return this.taskId;
        }),
        switchMap(
          taskId => taskId ? this.anreService.getAnreTask(taskId) : of(
            {
              companyDTO: {
                id: null,
                code: '',
                number: '',
                name: '',
                phoneNumber: '',
                mobileNumber: '',
                fax: '',
                email: '',
                legalForm: '',
                addressDTO: {
                  county: '',
                  city: '',
                  streetName: '',
                  streetNumber: '',
                  block: '',
                  floor: '',
                  apartment: '',
                  extendedAddress: '',
                  postalCode: ''
                },
                correspondenceAddressDTO: {
                  county: '',
                  city: '',
                  streetName: '',
                  streetNumber: '',
                  block: '',
                  floor: '',
                  apartment: '',
                  extendedAddress: '',
                  postalCode: ''
                }
              },
              firstName: '',
              lastName: '',
              cnp: '',
              licenseGas: '',
              licenseElectricity: ''
            })
        )
      ).subscribe((validationDetails) => this.initializeFormFields(validationDetails));
  }

  initializeFormFields(formValue: any): void {
    if (formValue) {
      const {
        id,
        companyDTO,
        firstName,
        lastName,
        cnp,
        documents,
        licenseGas,
        licenseElectricity
      }
        = formValue.information;

      this.validationDetails = {
        ...companyDTO.addressDTO,
        ...companyDTO,
        firstName,
        lastName,
        cnp,
        documents,
        licenseGas,
        licenseElectricity
      };
      this.validationDetails.companyId = companyDTO.id;
      this.validationDetails.id = id;

      Object.keys(this.validationForm.controls).forEach(
        key => {
          if (this.validationDetails[key]) {
            this.validationForm.controls[key].setValue(this.validationDetails[key]);
          }
          if (!this.editable || (key !== 'validated' && key !== 'invalidationReason')) {
            this.validationForm.controls[key].disable();
          }
        }
      );

      if (companyDTO) {
        this.validationForm.patchValue({
          correspondenceCounty: companyDTO.correspondenceAddressDTO.county,
          correspondenceCity: companyDTO.correspondenceAddressDTO.city,
          correspondenceStreetName: companyDTO.correspondenceAddressDTO.streetName,
          correspondenceStreetNumber: companyDTO.correspondenceAddressDTO.streetNumber,
          correspondenceBlock: companyDTO.correspondenceAddressDTO.block,
          correspondenceFloor: companyDTO.correspondenceAddressDTO.floor,
          correspondenceApartment: companyDTO.correspondenceAddressDTO.apartment,
          correspondenceExtendedAddress: companyDTO.correspondenceAddressDTO.extendedAddress,
          correspondencePostalCode: companyDTO.correspondenceAddressDTO.postalCode,
        });
      }
      this.validationForm.patchValue({
        validated: formValue.isValidatedAndAdminRoleAssigned,
        invalidationReason: formValue.unvalidatedReason
      });
      this.cities = [];
      this.citiesCorrespondence = [];
      this.addressService.getCities(this.validationForm.get('county').value)
        .subscribe((cities: CityDTO[]) => this.cities = cities);
      this.addressService.getCities(this.validationForm.get('correspondenceCounty').value)
        .subscribe(citiesCorrespondence => this.citiesCorrespondence = citiesCorrespondence);
      console.log("documenttype:", typeof documents);
      console.log("documentvalue:", JSON.stringify(documents));
      const files: string[] = documents.toString().split(',');
      for (const file of files) {
        const pathElements: string[] = file.split('/');
        this.fileList.push({
          name: pathElements.pop(),
          url: file,
          size: null
        });
      }
    }
  }

  toggleValidated(): void {
    this.isValidated = !this.isValidated;
  }

  saveValidationDetails(): void {
    if (this.validationDetails) {
      const formData = this.validationForm.getRawValue();

      const addressDTO: Address = {
        city: formData.city,
        extendedAddress: formData.extendedAddress,
        postalCode: formData.postalCode,
        county: formData.county,
        streetNumber: formData.streetNumber,
        streetName: formData.streetName,
        block: formData.block,
        floor: formData.floor,
        apartment: formData.apartment
      };

      const correspondenceAddressDTO: Address = {
        city: formData.correspondenceCity,
        extendedAddress: formData.correspondenceExtendedAddress,
        postalCode: formData.correspondencePostalCode,
        county: formData.correspondenceCounty,
        streetNumber: formData.correspondenceStreetNumber,
        streetName: formData.correspondenceStreetName,
        block: formData.correspondenceBlock,
        floor: formData.correspondenceFloor,
        apartment: formData.correspondenceApartment
      };

      const validationDetails = {
        id: this.validationDetails.id,
        companyDTO: {
          id: this.validationDetails.companyId,
          addressDTO,
          correspondenceAddressDTO,
          fax: formData.fax,
          name: formData.name,
          email: formData.email,
          legalForm: formData.legalForm,
          mobileNumber: formData.mobileNumber,
          phoneNumber: formData.phoneNumber,
          code: formData.cui,
          cui: formData.cui,
          onrcRegistrationNumber: formData.onrcRegistrationNumber
        },
        firstName: formData.firstName,
        lastName: formData.lastName,
        cnp: formData.cnp,
        validatedAndAdminRoleAssigned: formData.validated,
        licenseGas: formData.licenseGas,
        licenseElectricity: formData.licenseElectricity,
        unvalidatedReason: formData.invalidationReason
      };

      this.anreService.resolveAnreTask(this.taskId, {
        id: this.taskId,
        resolution: JSON.stringify(validationDetails)
      }).subscribe(
        v => this.router.navigateByUrl('/anre/task-uri')
      );
    }
  }

  ngOnDestroy(): void {
    this.routeDataSub.unsubscribe();
  }

}
